// Chemin: C:\gestion-copro\src\services\evenement.service.ts
// Service Firestore pour le module Calendrier / Agenda
// Session 94 : Création initiale
// Collection : coproprietes/{coproprieteId}/evenements/{evenementId}

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Evenement,
  EvenementFormData,
  AlerteEvenement,
  TypeEvenement,
  VisibiliteEvenement,
} from '../types/evenement.types';

// ============================================
// RÉFÉRENCE COLLECTION
// ============================================

const getEvenementsRef = (coproprieteId: string) =>
  collection(db, 'coproprietes', coproprieteId, 'evenements');

// ============================================
// CONVERSION FORM → FIRESTORE
// ============================================

/**
 * Convertit les données du formulaire en objet Firestore
 */
const formToFirestore = (
  formData: EvenementFormData,
  coproprieteId: string,
  userId: string,
  isUpdate: boolean = false
): Omit<Evenement, 'id'> => {
  // Construire dateDebut avec heure si fournie
  const dateDebutStr = formData.heureDebut
    ? `${formData.dateDebut}T${formData.heureDebut}:00`
    : `${formData.dateDebut}T00:00:00`;
  const dateDebut = Timestamp.fromDate(new Date(dateDebutStr));

  // Construire dateFin si fournie
  let dateFin: Timestamp | undefined;
  if (formData.dateFin) {
    const dateFinStr = formData.heureFin
      ? `${formData.dateFin}T${formData.heureFin}:00`
      : `${formData.dateFin}T23:59:59`;
    dateFin = Timestamp.fromDate(new Date(dateFinStr));
  }

  // Construire les alertes
  const alertes: AlerteEvenement[] = [];
  if (formData.alerteJ30) {
    alertes.push({ joursAvant: 30, statut: 'a_envoyer' });
  }
  if (formData.alerteJ7) {
    alertes.push({ joursAvant: 7, statut: 'a_envoyer' });
  }
  if (formData.alerteJ1) {
    alertes.push({ joursAvant: 1, statut: 'a_envoyer' });
  }

  const evenement: any = {
    titre: formData.titre.trim(),
    description: formData.description.trim() || null,
    dateDebut,
    dateFin: dateFin || null,
    type: formData.type,
    sousTypeGarantie: formData.type === 'echeance_garantie' && formData.sousTypeGarantie
      ? formData.sousTypeGarantie
      : null,
    recurrence: formData.recurrence,
    visibilite: formData.visibilite,
    alertes,
    affaireId: formData.affaireId || null,
    interlocuteurId: formData.interlocuteurId || null,
    coproprieteId,
    updatedAt: Timestamp.now(),
  };

  if (!isUpdate) {
    evenement.createdBy = userId;
    evenement.createdAt = Timestamp.now();
  }

  return evenement;
};

// ============================================
// CONVERSION FIRESTORE → FORM
// ============================================

/**
 * Convertit un document Firestore en données de formulaire
 */
export const firestoreToForm = (evenement: Evenement): EvenementFormData => {
  const dateDebut = evenement.dateDebut.toDate();
  const dateFin = evenement.dateFin?.toDate();

  // Extraire date et heure
  const formatDate = (d: Date) => d.toISOString().split('T')[0];
  const formatHeure = (d: Date) => {
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    return h === '00' && m === '00' ? '' : `${h}:${m}`;
  };

  // Vérifier quelles alertes sont actives
  const alerteJours = evenement.alertes.map(a => a.joursAvant);

  return {
    titre: evenement.titre,
    description: evenement.description || '',
    dateDebut: formatDate(dateDebut),
    heureDebut: formatHeure(dateDebut),
    dateFin: dateFin ? formatDate(dateFin) : '',
    heureFin: dateFin ? formatHeure(dateFin) : '',
    type: evenement.type,
    sousTypeGarantie: evenement.sousTypeGarantie || '',
    recurrence: evenement.recurrence,
    visibilite: evenement.visibilite,
    alerteJ30: alerteJours.includes(30),
    alerteJ7: alerteJours.includes(7),
    alerteJ1: alerteJours.includes(1),
    affaireId: evenement.affaireId || '',
    interlocuteurId: evenement.interlocuteurId || '',
  };
};

// ============================================
// CRUD
// ============================================

/**
 * Créer un événement
 */
export const createEvenement = async (
  coproprieteId: string,
  formData: EvenementFormData,
  userId: string
): Promise<string> => {
  const data = formToFirestore(formData, coproprieteId, userId);
  const ref = await addDoc(getEvenementsRef(coproprieteId), data);
  return ref.id;
};

/**
 * Modifier un événement
 */
export const updateEvenement = async (
  coproprieteId: string,
  evenementId: string,
  formData: EvenementFormData,
  userId: string
): Promise<void> => {
  const data = formToFirestore(formData, coproprieteId, userId, true);
  const ref = doc(db, 'coproprietes', coproprieteId, 'evenements', evenementId);
  await updateDoc(ref, data);
};

/**
 * Supprimer un événement
 */
export const deleteEvenement = async (
  coproprieteId: string,
  evenementId: string
): Promise<void> => {
  const ref = doc(db, 'coproprietes', coproprieteId, 'evenements', evenementId);
  await deleteDoc(ref);
};

/**
 * Récupérer un événement par ID
 */
export const getEvenement = async (
  coproprieteId: string,
  evenementId: string
): Promise<Evenement | null> => {
  const ref = doc(db, 'coproprietes', coproprieteId, 'evenements', evenementId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Evenement;
};

// ============================================
// REQUÊTES CALENDRIER
// ============================================

/**
 * Récupérer tous les événements d'une copropriété
 * (triés par dateDebut croissante)
 */
export const getAllEvenements = async (
  coproprieteId: string
): Promise<Evenement[]> => {
  const q = query(
    getEvenementsRef(coproprieteId),
    orderBy('dateDebut', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Evenement));
};

/**
 * Récupérer les événements d'un mois donné
 * @param annee - ex: 2026
 * @param mois - 0-indexé (0 = janvier, 1 = février...)
 */
export const getEvenementsMois = async (
  coproprieteId: string,
  annee: number,
  mois: number
): Promise<Evenement[]> => {
  const debut = Timestamp.fromDate(new Date(annee, mois, 1, 0, 0, 0));
  const fin = Timestamp.fromDate(new Date(annee, mois + 1, 0, 23, 59, 59));

  const q = query(
    getEvenementsRef(coproprieteId),
    where('dateDebut', '>=', debut),
    where('dateDebut', '<=', fin),
    orderBy('dateDebut', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Evenement));
};

/**
 * Récupérer les événements entre deux dates
 * (utile pour les vues semaine ou période personnalisée)
 */
export const getEvenementsPlage = async (
  coproprieteId: string,
  dateDebut: Date,
  dateFin: Date
): Promise<Evenement[]> => {
  const tsDebut = Timestamp.fromDate(dateDebut);
  const tsFin = Timestamp.fromDate(dateFin);

  const q = query(
    getEvenementsRef(coproprieteId),
    where('dateDebut', '>=', tsDebut),
    where('dateDebut', '<=', tsFin),
    orderBy('dateDebut', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Evenement));
};

/**
 * Récupérer les prochains événements (pour le widget Dashboard)
 * @param max - nombre max d'événements à retourner
 */
export const getProchainEvenements = async (
  coproprieteId: string,
  max: number = 5
): Promise<Evenement[]> => {
  const maintenant = Timestamp.now();

  const q = query(
    getEvenementsRef(coproprieteId),
    where('dateDebut', '>=', maintenant),
    orderBy('dateDebut', 'asc'),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Evenement));
};

/**
 * Récupérer les événements par type
 * (ex: toutes les échéances garantie pour le suivi GPA)
 */
export const getEvenementsParType = async (
  coproprieteId: string,
  type: TypeEvenement
): Promise<Evenement[]> => {
  const q = query(
    getEvenementsRef(coproprieteId),
    where('type', '==', type),
    orderBy('dateDebut', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Evenement));
};

/**
 * Récupérer les événements visibles pour un rôle donné
 * (filtre côté client car Firestore ne supporte pas OR natif sur un champ)
 */
export const getEvenementsVisibles = (
  evenements: Evenement[],
  role: 'coordinateur' | 'cs' | 'coproprietaire'
): Evenement[] => {
  return evenements.filter(evt => {
    if (role === 'coordinateur') return true; // Voit tout
    if (role === 'cs') return evt.visibilite !== 'coordinateur_only';
    // Copropriétaire : uniquement 'tous'
    return evt.visibilite === 'tous';
  });
};

// ============================================
// UTILITAIRES
// ============================================

/**
 * Calculer le nombre de jours restants avant un événement
 */
export const joursRestants = (dateDebut: Timestamp): number => {
  const maintenant = new Date();
  const cible = dateDebut.toDate();
  const diff = cible.getTime() - maintenant.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

/**
 * Déterminer la couleur d'urgence selon les jours restants
 */
export const getCouleurUrgence = (jours: number): string => {
  if (jours < 0) return 'text-gray-400';    // Passé
  if (jours <= 1) return 'text-red-600';     // Imminent
  if (jours <= 7) return 'text-orange-500';  // Cette semaine
  if (jours <= 30) return 'text-amber-500';  // Ce mois
  return 'text-green-500';                    // Serein
};

/**
 * Formater la date d'un événement pour affichage
 */
export const formaterDateEvenement = (dateDebut: Timestamp, dateFin?: Timestamp): string => {
  const debut = dateDebut.toDate();
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };

  const heures = debut.getHours();
  const minutes = debut.getMinutes();
  let result = debut.toLocaleDateString('fr-FR', options);

  // Ajouter l'heure si elle est significative (pas 00:00)
  if (heures !== 0 || minutes !== 0) {
    result += ` à ${heures.toString().padStart(2, '0')}h${minutes.toString().padStart(2, '0')}`;
  }

  // Ajouter la date de fin si différente
  if (dateFin) {
    const fin = dateFin.toDate();
    if (fin.toDateString() !== debut.toDateString()) {
      result += ` → ${fin.toLocaleDateString('fr-FR', options)}`;
    }
  }

  return result;
};

/**
 * Formater la date courte (pour le calendrier mensuel)
 */
export const formaterDateCourte = (timestamp: Timestamp): string => {
  const d = timestamp.toDate();
  const h = d.getHours();
  const m = d.getMinutes();
  if (h === 0 && m === 0) return ''; // Journée entière
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};
